import type { QueryClient } from '@tanstack/react-query';

/** What the shell's QueryClient learned from a failed request, for the session machine. */
export type SessionSignal =
  | { type: 'unauthenticated'; message: string | null }
  | { type: 'userChanged' };

type SessionSignalListener = (signal: SessionSignal) => void;

export interface SessionSignals {
  /** False when no session machine listens, so the caller keeps its own fallback (a reload). */
  emit: (signal: SessionSignal) => boolean;
  listen: (listener: SessionSignalListener) => () => void;
}

export const createSessionSignals = (): SessionSignals => {
  const listeners = new Set<SessionSignalListener>();
  return {
    emit: (signal) => {
      listeners.forEach((listener) => listener(signal));
      return listeners.size > 0;
    },
    listen: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
};

// Keyed by the shell's QueryClient, which the session machine reads with useQueryClient.
const signalsByClient = new WeakMap<QueryClient, SessionSignals>();

export const registerSessionSignals = (client: QueryClient, signals: SessionSignals): void => {
  signalsByClient.set(client, signals);
};

export const sessionSignalsOf = (client: QueryClient): SessionSignals | undefined =>
  signalsByClient.get(client);
